/**
 * Workflow Templates Page
 * Predefined checklists for Year-End, BAS Prep, New Client Onboarding, Audit Prep.
 * Users can apply templates to clients and track progress.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Circle, Clock, Users, FileText, Loader2, ChevronDown, ChevronRight, Play, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/client';
import { toast } from 'sonner';

interface WorkflowTask {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  category: string;
}

interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  taskCount: number;
  tasks: WorkflowTask[];
}

const TEMPLATES: WorkflowTemplate[] = [
  {
    id: 'year-end',
    name: 'Year-End Checklist',
    description: 'Complete year-end process for a client before 30 June',
    taskCount: 15,
    tasks: [
      { id: 'ye-1', title: 'Review Xero P&L for completeness', description: 'Ensure all income and expenses are recorded', completed: false, category: 'Review' },
      { id: 'ye-2', title: 'Reconcile all bank accounts', description: 'Check bank feeds match statements', completed: false, category: 'Review' },
      { id: 'ye-3', title: 'Review aged receivables', description: 'Write off bad debts if needed', completed: false, category: 'Review' },
      { id: 'ye-4', title: 'Review aged payables', description: 'Ensure all bills are entered', completed: false, category: 'Review' },
      { id: 'ye-5', title: 'Prepay eligible expenses', description: 'Identify and process prepayments before 30 June', completed: false, category: 'Tax Planning' },
      { id: 'ye-6', title: 'Top up superannuation', description: 'Maximise super contributions before deadline', completed: false, category: 'Tax Planning' },
      { id: 'ye-7', title: 'Review asset register', description: 'Identify assets eligible for instant write-off', completed: false, category: 'Tax Planning' },
      { id: 'ye-8', title: 'Check Division 7A compliance', description: 'Review director/shareholder loan accounts', completed: false, category: 'Compliance' },
      { id: 'ye-9', title: 'Verify super guarantee payments', description: 'Ensure Q4 SG paid by 28 July', completed: false, category: 'Compliance' },
      { id: 'ye-10', title: 'Prepare depreciation schedule', description: 'Calculate current year depreciation', completed: false, category: 'Preparation' },
      { id: 'ye-11', title: 'Prepare financial statements', description: 'Generate P&L, Balance Sheet, Notes', completed: false, category: 'Preparation' },
      { id: 'ye-12', title: 'Prepare tax return workpapers', description: 'Reconcile accounting profit to taxable income', completed: false, category: 'Preparation' },
      { id: 'ye-13', title: 'Review trust distribution resolution', description: 'Ensure trustee resolution signed before 30 June', completed: false, category: 'Compliance' },
      { id: 'ye-14', title: 'Send year-end report to client', description: 'Generate and email client summary', completed: false, category: 'Client' },
      { id: 'ye-15', title: 'Lodge tax return', description: 'Submit to ATO via tax agent portal', completed: false, category: 'Lodgement' },
    ],
  },
  {
    id: 'bas-prep',
    name: 'BAS Preparation',
    description: 'Quarterly BAS lodgement checklist',
    taskCount: 10,
    tasks: [
      { id: 'bas-1', title: 'Reconcile bank accounts for quarter', description: 'Ensure all transactions categorised', completed: false, category: 'Reconciliation' },
      { id: 'bas-2', title: 'Review GST on all transactions', description: 'Check GST codes are correct', completed: false, category: 'GST' },
      { id: 'bas-3', title: 'Check for missing invoices', description: 'Cross-reference with bank deposits', completed: false, category: 'GST' },
      { id: 'bas-4', title: 'Review GST on imports', description: 'Check Deferred GST scheme if applicable', completed: false, category: 'GST' },
      { id: 'bas-5', title: 'Calculate PAYG withholding', description: 'Verify wages and tax withheld match', completed: false, category: 'PAYG' },
      { id: 'bas-6', title: 'Calculate PAYG instalment', description: 'Check instalment amount or vary if needed', completed: false, category: 'PAYG' },
      { id: 'bas-7', title: 'Run BAS Validator in RoniDoc', description: 'Compare calculated vs actual BAS figures', completed: false, category: 'Validation' },
      { id: 'bas-8', title: 'Review fuel tax credits', description: 'Calculate if eligible', completed: false, category: 'GST' },
      { id: 'bas-9', title: 'Prepare BAS for lodgement', description: 'Enter figures in ATO portal', completed: false, category: 'Lodgement' },
      { id: 'bas-10', title: 'Lodge BAS and arrange payment', description: 'Submit and confirm payment method', completed: false, category: 'Lodgement' },
    ],
  },
  {
    id: 'new-client',
    name: 'New Client Onboarding',
    description: 'Steps to onboard a new accounting client',
    taskCount: 12,
    tasks: [
      { id: 'nc-1', title: 'Send engagement letter', description: 'Include scope, fees, terms', completed: false, category: 'Setup' },
      { id: 'nc-2', title: 'Collect signed engagement letter', description: 'Store in client file', completed: false, category: 'Setup' },
      { id: 'nc-3', title: 'Obtain ABN and TFN', description: 'Verify with ABN Lookup', completed: false, category: 'Setup' },
      { id: 'nc-4', title: 'Set up client in RoniDoc', description: 'Create client record with ABN, industry, contact details', completed: false, category: 'Setup' },
      { id: 'nc-5', title: 'Connect Xero organisation', description: 'OAuth connect and map to client', completed: false, category: 'Integration' },
      { id: 'nc-6', title: 'Review Chart of Accounts', description: 'Ensure accounts are properly set up', completed: false, category: 'Review' },
      { id: 'nc-7', title: 'Collect prior year financials', description: 'Request previous tax returns, financial statements', completed: false, category: 'Documents' },
      { id: 'nc-8', title: 'Upload documents to RoniDoc', description: 'Upload tax returns, contracts, insurance certificates', completed: false, category: 'Documents' },
      { id: 'nc-9', title: 'Run ATO compliance check', description: 'Check client against industry benchmarks', completed: false, category: 'Review' },
      { id: 'nc-10', title: 'Set up BAS/IAS lodgement schedule', description: 'Determine quarterly or monthly lodgement', completed: false, category: 'Setup' },
      { id: 'nc-11', title: 'Register as tax agent for client', description: 'Lodge form with ATO', completed: false, category: 'Setup' },
      { id: 'nc-12', title: 'Schedule introductory meeting', description: 'Discuss services, expectations, communication preferences', completed: false, category: 'Client' },
    ],
  },
  {
    id: 'audit-prep',
    name: 'Audit Preparation',
    description: 'Prepare client for ATO audit or review',
    taskCount: 11,
    tasks: [
      { id: 'ap-1', title: 'Review ATO correspondence', description: 'Understand scope of audit/review', completed: false, category: 'Review' },
      { id: 'ap-2', title: 'Run RoniDoc Audit Risk check', description: 'Identify specific risk areas', completed: false, category: 'Analysis' },
      { id: 'ap-3', title: 'Review all BAS lodgements', description: 'Check for errors or discrepancies', completed: false, category: 'Review' },
      { id: 'ap-4', title: 'Verify income completeness', description: 'Cross-reference bank deposits with reported income', completed: false, category: 'Review' },
      { id: 'ap-5', title: 'Review expense substantiation', description: 'Ensure all deductions have supporting documents', completed: false, category: 'Documents' },
      { id: 'ap-6', title: 'Check motor vehicle logbook', description: 'Verify logbook is current and complete', completed: false, category: 'Documents' },
      { id: 'ap-7', title: 'Review contractor payments (TPAR)', description: 'Verify TPAR lodged if applicable', completed: false, category: 'Compliance' },
      { id: 'ap-8', title: 'Check super guarantee compliance', description: 'Verify all SG paid on time', completed: false, category: 'Compliance' },
      { id: 'ap-9', title: 'Prepare response to ATO', description: 'Draft letter addressing audit scope', completed: false, category: 'Response' },
      { id: 'ap-10', title: 'Gather supporting documents', description: 'Compile all requested documents', completed: false, category: 'Documents' },
      { id: 'ap-11', title: 'Submit response to ATO', description: 'Lodge via MyGov or tax agent portal', completed: false, category: 'Response' },
    ],
  },
];

export default function WorkflowTemplatesPage() {
  const queryClient = useQueryClient();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [activeWorkflows, setActiveWorkflows] = useState<Record<string, { templateId: string; clientName: string; tasks: WorkflowTask[]; startedAt: string }>>(() => {
    const saved = localStorage.getItem('ronidoc-workflows');
    return saved ? JSON.parse(saved) : {};
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-list'],
    queryFn: async () => { const r = await apiClient.get('/clients?limit=200'); return r.data.data; },
  });

  const clients = clientsData?.clients || [];

  const saveWorkflows = (wf: typeof activeWorkflows) => {
    setActiveWorkflows(wf);
    localStorage.setItem('ronidoc-workflows', JSON.stringify(wf));
  };

  const startWorkflow = (templateId: string) => {
    if (!selectedClient) { toast.error('Select a client first'); return; }
    const template = TEMPLATES.find(t => t.id === templateId);
    if (!template) return;
    const client = clients.find((c: any) => c.id === selectedClient);
    const key = `${templateId}-${selectedClient}`;
    const updated = {
      ...activeWorkflows,
      [key]: {
        templateId,
        clientName: client?.displayName || client?.display_name || 'Unknown',
        tasks: template.tasks.map(t => ({ ...t, completed: false })),
        startedAt: new Date().toISOString(),
      },
    };
    saveWorkflows(updated);
    toast.success(`Started "${template.name}" for ${client?.displayName || client?.display_name}`);
  };

  const toggleTask = (workflowKey: string, taskId: string) => {
    const wf = { ...activeWorkflows };
    const workflow = wf[workflowKey];
    if (!workflow) return;
    workflow.tasks = workflow.tasks.map(t => t.id === taskId ? { ...t, completed: !t.completed } : t);
    saveWorkflows(wf);
  };

  const deleteWorkflow = (key: string) => {
    const wf = { ...activeWorkflows };
    delete wf[key];
    saveWorkflows(wf);
    toast.success('Workflow removed');
  };

  const activeKeys = Object.keys(activeWorkflows);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FileText}
        title="Workflow Templates"
        description="Reusable checklists for common accounting tasks"
      />


      {/* Start New Workflow */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Start a Workflow</CardTitle>
          <CardDescription>Select a template and client to begin</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="flex-1"><SelectValue placeholder="Select client..." /></SelectTrigger>
              <SelectContent>
                {clients.map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>{c.displayName || c.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 flex-wrap">
              {TEMPLATES.map(t => (
                <Button key={t.id} variant="outline" size="sm" onClick={() => startWorkflow(t.id)} disabled={!selectedClient}>
                  <Play className="h-3 w-3 mr-1" /> {t.name}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Workflows */}
      {activeKeys.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Active Workflows</h2>
          {activeKeys.map(key => {
            const wf = activeWorkflows[key];
            const template = TEMPLATES.find(t => t.id === wf.templateId);
            const completed = wf.tasks.filter(t => t.completed).length;
            const total = wf.tasks.length;
            const pct = Math.round((completed / total) * 100);
            const [expanded, setExpanded] = useState(false);

            return (
              <Card key={key}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
                      {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      <div>
                        <CardTitle className="text-sm">{template?.name} - {wf.clientName}</CardTitle>
                        <CardDescription className="text-xs">Started {new Date(wf.startedAt).toLocaleDateString('en-AU')}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <span className="text-sm font-medium">{completed}/{total}</span>
                        <Progress value={pct} className="w-20 h-1.5 mt-1" />
                      </div>
                      {pct === 100 && <Badge className="text-[10px]">Complete</Badge>}
                      <Button variant="ghost" size="sm" onClick={() => deleteWorkflow(key)}>
                        <RotateCcw className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {expanded && (
                  <CardContent className="pt-0">
                    <div className="space-y-1">
                      {wf.tasks.map(task => (
                        <div
                          key={task.id}
                          onClick={() => toggleTask(key, task.id)}
                          className={cn(
                            'flex items-start gap-3 p-2 rounded-lg cursor-pointer transition hover:bg-muted/30',
                            task.completed && 'opacity-50'
                          )}
                        >
                          {task.completed ? (
                            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          ) : (
                            <Circle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={cn('text-sm', task.completed && 'line-through')}>{task.title}</p>
                            <p className="text-xs text-muted-foreground">{task.description}</p>
                          </div>
                          <Badge variant="outline" className="text-[9px] shrink-0">{task.category}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Template Library */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Template Library</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TEMPLATES.map(t => (
            <Card key={t.id} className="hover:border-primary/30 transition cursor-pointer" onClick={() => setSelectedTemplate(selectedTemplate === t.id ? null : t.id)}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  {t.name}
                </CardTitle>
                <CardDescription className="text-xs">{t.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{t.taskCount} tasks</span>
                  <div className="flex gap-1">
                    {[...new Set(t.tasks.map(task => task.category))].map(cat => (
                      <Badge key={cat} variant="outline" className="text-[8px]">{cat}</Badge>
                    ))}
                  </div>
                </div>
                {selectedTemplate === t.id && (
                  <div className="mt-3 pt-3 border-t space-y-1">
                    {t.tasks.map(task => (
                      <div key={task.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Circle className="h-3 w-3 shrink-0" />
                        <span>{task.title}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
